import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Button, Empty, Select, Spin, Space, Switch, Tooltip, Typography } from 'antd';
import { ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';

const { Title } = Typography;

const WEEK_OPTIONS = [
  { label: '8 Weeks',  value: 8  },
  { label: '13 Weeks', value: 13 },
  { label: '26 Weeks', value: 26 },
  { label: '52 Weeks', value: 52 },
];

function fmtPct(n) {
  if (n == null) return '—';
  return `${Number(n).toFixed(1)}%`;
}

function fmtNum(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

function fmtMmSs(secs) {
  if (secs == null || isNaN(secs)) return '—';
  const total = Math.round(Number(secs));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const HEADER_BG = '#1a3d6b';
const TOTAL_BG  = '#eef3f9';
const ALT_BG    = '#f5f8fc';

const thStyle = (extra = {}) => ({
  background:    HEADER_BG,
  color:         '#fff',
  fontWeight:    700,
  fontSize:      12,
  padding:       '7px 10px',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  whiteSpace:    'nowrap',
  borderBottom:  '2px solid #0e2a4e',
  ...extra,
});

const tdBase = {
  padding:            '8px 10px',
  borderBottom:       '1px solid #eef0f2',
  verticalAlign:      'top',
  fontVariantNumeric: 'tabular-nums',
};

export default function CampaignWeeklyScorecardPage() {
  useContext(UserContext);
  // Starts true: the page fetches campaign options and then the scorecard, and
  // without this the spinner only appears for the second request, leaving the
  // first few seconds looking like an empty table rather than a loading one.
  const [loading,           setLoading]           = useState(true);
  const [optionsLoaded,     setOptionsLoaded]     = useState(false);
  const [campaigns,         setCampaigns]         = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [weeks,             setWeeks]             = useState(8);
  const [data,              setData]              = useState([]);
  // By Skill Group: a row per active Skill Group (Admin > Business Config)
  // under its campaign, with the call figures only -- Occupancy and In
  // Available come from a table with no skill.
  const [skillGroups,       setSkillGroups]       = useState([]);
  const [bySkill,           setBySkill]           = useState(false);
  const [groupData,         setGroupData]         = useState([]);

  useEffect(() => {
    api.get('/api/skill-groups')
      .then((r) => setSkillGroups(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSkillGroups([]));
  }, []);

  useEffect(() => {
    api.get('/api/campaign-stats/options')
      .then(r => {
        const list = r.data.campaigns || [];
        setCampaigns(list);
        // Only one campaign: picked, as on the other Interactions pages.
        if (list.length === 1) setSelectedCampaigns((cur) => (cur.length ? cur : [list[0].value]));
      })
      .catch(() => setCampaigns([]))
      .finally(() => setOptionsLoaded(true));
  }, []);

  const fetchData = useCallback(async () => {
    // Options still in flight -- stay loading rather than dropping the spinner
    if (!optionsLoaded) return;
    const campaignFilter = selectedCampaigns.length > 0
      ? selectedCampaigns
      : campaigns.map(c => c.value);
    if (campaignFilter.length === 0) {
      // Options came back empty, so nothing will ever load. Stop spinning.
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const startDate = dayjs().subtract(weeks, 'week').startOf('week').format('YYYY-MM-DD');
      const params = { start_date: startDate, campaign: campaignFilter.join(',') };
      const [r, gr] = await Promise.all([
        api.get('/api/campaign-stats/scorecard', { params }),
        bySkill ? api.get('/api/campaign-stats/scorecard-skill-groups', { params }).catch(() => ({ data: [] }))
                : Promise.resolve({ data: [] }),
      ]);
      setData(r.data || []);
      setGroupData(Array.isArray(gr.data) ? gr.data : []);
    } catch (_) {
      setData([]);
      setGroupData([]);
    } finally {
      setLoading(false);
    }
  }, [weeks, selectedCampaigns, campaigns, optionsLoaded, bySkill]);

  // The toggle is offered only when a campaign in view has a Skill Group.
  const inView = new Set((selectedCampaigns.length ? selectedCampaigns : campaigns.map((c) => c.value)).map(String));
  const hasGroups = skillGroups.some((sg) => inView.has(String(sg.CXCampaignNo)));

  useEffect(() => { fetchData(); }, [fetchData]);

  const { weekList, campaignList, pivot, totals, groupRows } = useMemo(() => {
    const weekSet = new Set(data.map(r => r.WeekStart));
    const weekList = Array.from(weekSet).sort().reverse();

    const campaignMap = {};
    for (const r of data) {
      if (!campaignMap[r.CampaignNo])
        campaignMap[r.CampaignNo] = r.CampaignName || String(r.CampaignNo);
    }
    const campaignList = Object.entries(campaignMap)
      .map(([no, name]) => ({ no: Number(no), name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const pivot = {};
    for (const r of data) {
      if (!pivot[r.CampaignNo]) pivot[r.CampaignNo] = {};
      pivot[r.CampaignNo][r.WeekStart] = r;
    }

    const totals = {};
    for (const r of data) {
      if (!totals[r.WeekStart]) totals[r.WeekStart] = { Offered: 0, CallBacks: 0 };
      totals[r.WeekStart].Offered   += r.Offered   || 0;
      totals[r.WeekStart].CallBacks += r.CallBacks || 0;
    }

    // Skill Group rows by campaign, then group, then week. Not in the totals,
    // which stay campaign-only so nothing is counted twice.
    const groupRows = {};
    for (const r of groupData) {
      const byGroup = (groupRows[r.CampaignNo] ||= {});
      const grp = (byGroup[r.GroupID] ||= { name: r.GroupName, weeks: {} });
      grp.weeks[r.WeekStart] = r;
    }

    return { weekList, campaignList, pivot, totals, groupRows };
  }, [data, groupData]);

  // WeekStart is Sunday. A single date in the header said nothing about which
  // days it covered -- read as a week ending, a week starting or the day
  // itself, all equally plausible -- so both ends are shown. The year sits on
  // its own line, since it is the same for every column and repeating it six
  // times across the header earns nothing.
  const weekRange = (ws) => {
    const start = dayjs(ws);
    const end   = start.add(6, 'day');
    return { span: `${start.format('M/D')} - ${end.format('M/D')}`, year: end.format('YYYY') };
  };

  const STAT = { fontSize: 12, lineHeight: 1.7, whiteSpace: 'nowrap' };
  const LABEL = { color: '#8c8c8c', fontSize: 11 };

  function StatCell({ row }) {
    if (!row) return <td style={{ ...tdBase, textAlign: 'right', color: '#bfbfbf' }}>—</td>;
    const target = row.TargetSLA != null ? row.TargetSLA * 100 : null;
    const slaColor = val => target == null || val == null ? undefined : val >= target ? '#389e0d' : '#cf1322';
    return (
      <td style={{ ...tdBase, textAlign: 'right' }}>
        {target != null && (
          <div style={STAT}>
            <span style={LABEL}>Target SLA </span><strong style={{ color: '#595959' }}>{fmtPct(target)}</strong>
          </div>
        )}
        <div style={STAT}>
          <span style={LABEL}>Raw SLA </span><strong style={{ color: slaColor(row.RawSLA) }}>{fmtPct(row.RawSLA)}</strong>
        </div>
        <div style={STAT}>
          <span style={LABEL}>Cust SLA </span><strong style={{ color: slaColor(row.CustomerSLA) }}>{fmtPct(row.CustomerSLA)}</strong>
        </div>
        <div style={{ ...STAT, marginTop: 4 }}>
          <span style={LABEL}>Offered </span>{fmtNum(row.Offered)}
        </div>
        <div style={STAT}>
          <span style={LABEL}>Call Backs </span>{fmtNum(row.CallBacks)}
        </div>
        <div style={{ ...STAT, marginTop: 4 }}>
          <span style={LABEL}>ASA </span>{fmtMmSs(row.RawASA)}
        </div>
        <div style={STAT}>
          <span style={LABEL}>AHT </span>{fmtMmSs(row.RawAHT)}
        </div>
        {(row.OccupancyPct != null || row.InAvailablePct != null) && (() => {
          // Some campaigns share one staffing pool and are stored in dbalByHour
          // under a combined name. Occupancy and availability then describe the
          // whole pool, not this campaign alone -- mark them so the figure is
          // not read as campaign-specific.
          const pooled = row.DbalGroup && row.DbalGroup !== row.CampaignName;
          const others = pooled
            ? row.DbalGroup.split(',').map(s => s.trim()).filter(s => s && s !== row.CampaignName)
            : [];
          return (
            <div style={{ marginTop: 4 }}>
              <div style={STAT}>
                <span style={LABEL}>Occupancy </span>{fmtPct(row.OccupancyPct)}
              </div>
              <div style={STAT}>
                <span style={LABEL}>In Available </span>{fmtPct(row.InAvailablePct)}
              </div>
              {pooled && (
                <Tooltip title={`Staffed as one pool with ${others.join(', ')}. Occupancy and In Available cover the whole pool, so every campaign in it shows the same figures. Target SLA is the pool's too.`}>
                  <div style={{ ...STAT, color: '#d46b08', cursor: 'help' }}>
                    pooled&nbsp;<InfoCircleOutlined style={{ fontSize: 10 }} />
                  </div>
                </Tooltip>
              )}
            </div>
          );
        })()}
      </td>
    );
  }

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Campaign Weekly Scorecard</Title>
        <Space wrap>
          <Select
            mode="multiple"
            placeholder="All campaigns"
            style={{ minWidth: 220 }}
            options={campaigns}
            value={selectedCampaigns}
            onChange={setSelectedCampaigns}
            maxTagCount="responsive"
            allowClear
          />
          <Select
            value={weeks}
            onChange={setWeeks}
            options={WEEK_OPTIONS}
            style={{ width: 120 }}
          />
          {hasGroups && (
            <Space size={6}>
              <Switch size="small" checked={bySkill} onChange={setBySkill} />
              <span>By Skill Group</span>
            </Space>
          )}
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading} />
        </Space>
      </div>

      <Spin spinning={loading} tip="Loading campaign stats…" size="large">
        {/* An empty grid still renders a header and a lone Total row, which
            reads as broken rather than empty. Show nothing until there is
            something, and say so once loading has finished. */}
        {!loading && weekList.length === 0 ? (
          <Empty
            description="No campaign stats for the selected filters."
            style={{ padding: '48px 0' }}
          />
        ) : (
        <div style={{ overflowX: 'auto', borderRadius: 6, border: '1px solid #dce8f5', minHeight: 120 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th style={thStyle({ textAlign: 'left', position: 'sticky', left: 0, zIndex: 2, minWidth: 160, background: HEADER_BG })}>
                  Campaign
                </th>
                {weekList.map(w => {
                  const { span, year } = weekRange(w);
                  return (
                    <th key={w} style={thStyle({ textAlign: 'right', minWidth: 110 })}>
                      <div style={{ whiteSpace: 'nowrap' }}>{span}</div>
                      <div style={{ fontWeight: 400, fontSize: 11, opacity: 0.75 }}>{year}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {campaignList.map((camp, i) => {
                const bg = i % 2 === 0 ? '#fff' : ALT_BG;
                const groups = bySkill && hasGroups
                  ? Object.entries(groupRows[camp.no] || {}).sort((a, b) => a[1].name.localeCompare(b[1].name))
                  : [];
                return (
                  <React.Fragment key={camp.no}>
                  {/* By Skill Group: a campaign with groups shows only its
                      groups; one without keeps its own row. */}
                  {!groups.length && (
                  <tr style={{ background: bg }}>
                    <td style={{
                      ...tdBase,
                      position:   'sticky',
                      left:       0,
                      zIndex:     1,
                      background: bg,
                      fontWeight: 600,
                      fontSize:   13,
                      minWidth:   160,
                      verticalAlign: 'middle',
                    }}>
                      {camp.name}
                    </td>
                    {weekList.map(w => (
                      <StatCell key={w} row={pivot[camp.no]?.[w]} />
                    ))}
                  </tr>
                  )}
                  {/* The campaign's Target SLA, so the SLA colours still
                      mean against target. */}
                  {groups.map(([gid, grp]) => (
                    <tr key={`${camp.no}-${gid}`} style={{ background: bg }}>
                      <td style={{
                        ...tdBase, position: 'sticky', left: 0, zIndex: 1, background: bg,
                        fontWeight: 600, fontSize: 13, minWidth: 160, verticalAlign: 'middle',
                      }}>
                        {grp.name}
                        <div style={{ color: '#8c8c8c', fontSize: 11, fontWeight: 400 }}>{camp.name}</div>
                      </td>
                      {weekList.map(w => {
                        const gr = grp.weeks[w];
                        return <StatCell key={w} row={gr ? { ...gr, TargetSLA: pivot[camp.no]?.[w]?.TargetSLA } : null} />;
                      })}
                    </tr>
                  ))}
                  </React.Fragment>
                );
              })}

              {/* Totals: whole campaigns, so hidden while By Skill Group is
                  on (groups can overlap or leave skills out, and would not
                  add up to it). */}
              {!(bySkill && hasGroups) && (
              <tr style={{ background: TOTAL_BG, borderTop: '2px solid #b8d0e8' }}>
                <td style={{
                  ...tdBase,
                  position:   'sticky',
                  left:       0,
                  background: TOTAL_BG,
                  fontWeight: 700,
                  fontSize:   13,
                  minWidth:   160,
                  borderTop:  '2px solid #b8d0e8',
                  verticalAlign: 'middle',
                }}>
                  Total
                </td>
                {weekList.map(w => (
                  <td key={w} style={{ ...tdBase, textAlign: 'right', borderTop: '2px solid #b8d0e8' }}>
                    <div style={STAT}>
                      <span style={LABEL}>Offered </span><strong>{fmtNum(totals[w]?.Offered ?? null)}</strong>
                    </div>
                    <div style={STAT}>
                      <span style={LABEL}>Call Backs </span><strong>{fmtNum(totals[w]?.CallBacks ?? null)}</strong>
                    </div>
                  </td>
                ))}
              </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </Spin>
    </div>
  );
}
